/* Session storage.
 *
 * Standing ruling (d): SESSION = TOKEN. TIER = SERVER. The only thing kept
 * on the device is the opaque token the server issued. Identity, role, plan
 * and subscription state are never cached here and never read from storage
 * to make a decision -- that was the legacy bug where a browser owner could
 * edit localStorage and grant themselves a tier (inventory 6.6, 6.7).
 *
 * Storage prefix is `souqakar_`, per standing ruling (b). Nothing writes an
 * `ejarmasr_` key.
 *
 * Legacy's clearSession left `ejarmasr_current_user_email` behind and the
 * panels used it as their session id (inventory 6.9). There is nothing to
 * leave behind here: one key in, one key out.
 */

const TOKEN_KEY = "souqakar_session_token";

/** Notified whenever the token changes, including on 401 teardown. */
type Listener = (token: string | null) => void;
const listeners = new Set<Listener>();

export function getToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* storage disabled -- the session lives for this page only */
  }
  listeners.forEach((fn) => fn(token));
}

/** Removes every trace of the session. There is exactly one key. */
export function clearSession(): void {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
  listeners.forEach((fn) => fn(null));
}

export function onSessionChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* Where to send the visitor back after signing in. Kept in sessionStorage so
 * it dies with the tab rather than lingering. */
const RETURN_KEY = "souqakar_return_to";

export function setReturnPath(path: string): void {
  try { window.sessionStorage.setItem(RETURN_KEY, path); } catch { /* ignore */ }
}

export function takeReturnPath(): string | null {
  try {
    const v = window.sessionStorage.getItem(RETURN_KEY);
    window.sessionStorage.removeItem(RETURN_KEY);
    return v;
  } catch {
    return null;
  }
}
